/**
 * PreloadScene - Asset loading with cyberpunk terminal aesthetic
 * Features: segmented progress bar, boot phases, COMBAT READY completion
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../utils/ServiceLocator';
import { AudioManager } from '../managers/AudioManager';

// Boot phases for loading messages
const BOOT_PHASES = [
  'SYNCING ACCOUNT',
  'LOADING STAR MAP',
  'COMPILING FACTION DATA',
  'INITIALIZING FLEET SYSTEMS',
  'CALIBRATING SENSORS',
  'ENTERING SECTOR',
];

export class PreloadScene extends Phaser.Scene {
  private progressBar!: Phaser.GameObjects.Graphics;
  private progressBox!: Phaser.GameObjects.Graphics;
  private progressGlow!: Phaser.GameObjects.Graphics;
  private phaseText!: Phaser.GameObjects.Text;
  private percentText!: Phaser.GameObjects.Text;
  private skipButton!: Phaser.GameObjects.Text;

  private currentPhase: number = 0;
  private canSkip: boolean = false;

  constructor() {
    super({ key: 'PreloadScene' });
  }

  init(): void {
    console.log('PreloadScene: Starting asset load...');
    // Check if user has visited before (for skip functionality)
    this.canSkip = localStorage.getItem('crimsonmandate_visited') === 'true';
  }

  preload(): void {
    this.createLoadingUI();
    this.setupLoadEvents();
    this.loadAssets();
  }

  create(): void {
    console.log('PreloadScene: Assets loaded, transitioning to login...');

    // Start streaming background music (no upfront download)
    const audioManager = ServiceLocator.get<AudioManager>(Services.AUDIO);
    audioManager.playMusic();

    // Mark as visited and go straight to login
    localStorage.setItem('crimsonmandate_visited', 'true');
    this.transitionToLogin();
  }

  private createLoadingUI(): void {
    const { width, height } = this.cameras.main;
    const centerX = width / 2;
    const centerY = height / 2;

    // Starfield background (loaded inline for immediate display)
    this.load.image('bg-starfield', 'assets/bg-starfield-4k.png');
    this.load.once('filecomplete-image-bg-starfield', () => {
      const bg = this.add.image(centerX, centerY, 'bg-starfield');
      bg.setDisplaySize(width, height);
      bg.setDepth(-1);
    });

    // Shooting stars effect
    this.createShootingStars(width, height);

    // Logo image (loaded inline for immediate display)
    // Match login screen positioning: 15% from top
    const logoY = height * 0.15;
    
    this.load.image('logo', 'assets/crimson-mandate-logo.png?v=2');
    this.load.once('filecomplete-image-logo', () => {
      // Subtle glow layer (toned down 30%)
      const logoGlow = this.add.image(centerX, logoY, 'logo');
      const maxWidth = Math.min(800, width - 40);
      const scale = maxWidth / logoGlow.width;
      logoGlow.setScale(Math.min(scale, 1.3) * 1.02);
      logoGlow.setAlpha(0.2);
      logoGlow.setTint(0x00ffff);
      logoGlow.setDepth(1);

      // Main logo
      const logo = this.add.image(centerX, logoY, 'logo');
      logo.setScale(Math.min(scale, 1.3));
      logo.setDepth(2);

      // Subtitle under logo
      this.add.text(centerX, logoY + 80, 'PHASE MIGRATION v0.1', {
        fontFamily: 'Share Tech Mono, monospace',
        fontSize: '16px',
        color: '#5a8aaa',
        letterSpacing: 3,
        shadow: {
          offsetX: 1,
          offsetY: 1,
          color: '#000000',
          blur: 5,
          fill: true,
        },
      }).setOrigin(0.5).setDepth(2);
    });

    // Progress bar position (centered vertically)
    const barY = centerY + 40;
    const barWidth = 400;
    const barHeight = 20;

    // Progress box background with border
    this.progressBox = this.add.graphics();
    this.progressBox.fillStyle(0x001020, 0.8);
    this.progressBox.fillRect(centerX - barWidth / 2 - 2, barY - 2, barWidth + 4, barHeight + 4);
    this.progressBox.lineStyle(2, 0x00ffff, 0.4);
    this.progressBox.strokeRect(centerX - barWidth / 2 - 2, barY - 2, barWidth + 4, barHeight + 4);
    this.progressBox.setDepth(5);

    // Progress glow (behind bar)
    this.progressGlow = this.add.graphics();
    this.progressGlow.setDepth(4);

    // Progress bar (segmented look)
    this.progressBar = this.add.graphics();
    this.progressBar.setDepth(6);

    // Phase text (larger with shadow)
    this.phaseText = this.add.text(centerX, barY + 45, `> ${BOOT_PHASES[0]}`, {
      fontFamily: 'Share Tech Mono, monospace',
      fontSize: '28px',
      color: '#00ffff',
      letterSpacing: 3,
      shadow: {
        offsetX: 2,
        offsetY: 2,
        color: '#000000',
        blur: 8,
        fill: true,
      },
    });
    this.phaseText.setOrigin(0.5);
    this.phaseText.setDepth(10);

    // Percentage text with phase label (larger with shadow)
    this.percentText = this.add.text(centerX, barY + 85, '0% - INITIALIZING', {
      fontFamily: 'Share Tech Mono, monospace',
      fontSize: '20px',
      color: '#6a9aba',
      shadow: {
        offsetX: 1,
        offsetY: 1,
        color: '#000000',
        blur: 6,
        fill: true,
      },
    });
    this.percentText.setOrigin(0.5);
    this.percentText.setDepth(10);

    // Skip button (only if visited before)
    if (this.canSkip) {
      this.skipButton = this.add.text(width - 30, height - 30, '[ SKIP ]', {
        fontFamily: 'Share Tech Mono, monospace',
        fontSize: '14px',
        color: '#4a6a8a',
      });
      this.skipButton.setOrigin(1, 1);
      this.skipButton.setInteractive({ useHandCursor: true });
      this.skipButton.on('pointerover', () => this.skipButton.setColor('#00ffff'));
      this.skipButton.on('pointerout', () => this.skipButton.setColor('#4a6a8a'));
      this.skipButton.on('pointerdown', () => this.skipToLogin());
      this.skipButton.setDepth(100);
    }
  }

  private setupLoadEvents(): void {
    const { width, height } = this.cameras.main;
    const centerX = width / 2;
    const barY = height / 2 + 40;
    const barWidth = 400;
    const barHeight = 20;

    this.load.on('progress', (value: number) => {
      // Update progress bar with segmented look
      this.progressBar.clear();
      this.progressGlow.clear();

      const fillWidth = (barWidth - 4) * value;
      const segments = Math.floor(fillWidth / 8);

      // Glow effect
      this.progressGlow.fillStyle(0x00ffff, 0.2);
      this.progressGlow.fillRect(
        centerX - barWidth / 2,
        barY,
        fillWidth,
        barHeight
      );

      // Draw segments
      for (let i = 0; i < segments; i++) {
        const segX = centerX - barWidth / 2 + 2 + i * 8;
        this.progressBar.fillStyle(0x00ffff, 0.9);
        this.progressBar.fillRect(segX, barY + 2, 6, barHeight - 4);
      }

      // Update percentage text
      const percent = Math.round(value * 100);
      const phaseIndex = Math.min(
        Math.floor(value * BOOT_PHASES.length),
        BOOT_PHASES.length - 1
      );

      this.percentText.setText(`${percent}% - ${BOOT_PHASES[phaseIndex]}`);

      // Update phase text if changed
      if (phaseIndex !== this.currentPhase) {
        this.currentPhase = phaseIndex;
        this.updatePhaseText(BOOT_PHASES[phaseIndex]);
      }
    });

    this.load.on('complete', () => {
      // Final phase update
      this.updatePhaseText('ENTERING SECTOR');
      this.percentText.setText('100% - COMPLETE');
    });
  }

  private updatePhaseText(phase: string): void {
    // Flash red briefly, then green
    this.phaseText.setText(`> ${phase}`);
    this.phaseText.setColor('#ff4444');

    this.time.delayedCall(150, () => {
      if (this.phaseText) {
        this.phaseText.setColor('#00ff88');
      }
    });
  }

  private skipToLogin(): void {
    this.cameras.main.fadeOut(200, 0, 0, 0);
    this.time.delayedCall(200, () => {
      this.scene.start('MMOLoginScene');
    });
  }

  private transitionToLogin(): void {
    this.cameras.main.fadeOut(300, 0, 0, 0);
    this.time.delayedCall(300, () => {
      console.log('PreloadScene: Starting login scene...');
      this.scene.start('MMOLoginScene');
    });
  }

  private createShootingStars(width: number, height: number): void {
    this.time.addEvent({
      delay: 800,
      callback: () => this.spawnShootingStar(width, height),
      loop: true,
    });

    this.time.delayedCall(200, () => this.spawnShootingStar(width, height));
    this.time.delayedCall(600, () => this.spawnShootingStar(width, height));
  }

  private spawnShootingStar(width: number, height: number): void {
    const star = this.add.graphics();
    star.setDepth(0);

    const fromRight = Math.random() > 0.5;
    const startX = fromRight ? width + 50 : Phaser.Math.Between(100, width);
    const startY = fromRight ? Phaser.Math.Between(0, height * 0.5) : -50;
    const endX = startX - Phaser.Math.Between(300, 600);
    const endY = startY + Phaser.Math.Between(200, 400);
    const length = Phaser.Math.Between(40, 100);
    const alpha = Phaser.Math.FloatBetween(0.3, 0.7);

    this.tweens.add({
      targets: { progress: 0 },
      progress: 1,
      duration: Phaser.Math.Between(400, 800),
      ease: 'Cubic.easeIn',
      onUpdate: (tween) => {
        const p = tween.getValue() ?? 0;
        const currentX = startX + (endX - startX) * p;
        const currentY = startY + (endY - startY) * p;

        star.clear();

        const angle = Math.atan2(endY - startY, endX - startX);
        const tailX = currentX - Math.cos(angle) * length;
        const tailY = currentY - Math.sin(angle) * length;

        for (let i = 0; i < 5; i++) {
          const t = i / 5;
          const segStartX = tailX + (currentX - tailX) * t;
          const segStartY = tailY + (currentY - tailY) * t;
          const segEndX = tailX + (currentX - tailX) * (t + 0.25);
          const segEndY = tailY + (currentY - tailY) * (t + 0.25);

          star.lineStyle(2 - i * 0.3, 0xffffff, alpha * (1 - t * 0.7));
          star.moveTo(segStartX, segStartY);
          star.lineTo(segEndX, segEndY);
          star.strokePath();
        }

        star.fillStyle(0xffffff, alpha);
        star.fillCircle(currentX, currentY, 2);
      },
      onComplete: () => star.destroy(),
    });
  }

  private loadAssets(): void {
    // Sprites - SVG files
    this.load.svg('hex_tile', 'assets/sprites/hex_tile.svg', { width: 100, height: 86 });
    this.load.svg('planet', 'assets/sprites/planet.svg', { width: 60, height: 60 });
    this.load.svg('unit', 'assets/sprites/unit.svg', { width: 40, height: 40 });
    this.load.svg('selection', 'assets/sprites/selection.svg', { width: 100, height: 86 });
    this.load.svg('shield', 'assets/sprites/shield.svg', { width: 30, height: 30 });
    this.load.svg('tech_crate', 'assets/sprites/tech_crate.svg', { width: 40, height: 40 });

    // Ship images - PNG files
    this.load.image('scout', 'assets/ships/scout.png');
    this.load.image('fighter', 'assets/ships/fighter.png');
    this.load.image('cruiser', 'assets/ships/cruiser.png');
    this.load.image('tradeship', 'assets/ships/tradeship.png');
    this.load.image('tug', 'assets/ships/tug.png');
    this.load.image('battleship', 'assets/ships/battleship.png');
    this.load.image('bomber', 'assets/ships/bomber.png');
    this.load.image('carrier', 'assets/ships/carrier.png');
    this.load.image('destroyer', 'assets/ships/destroyer.png');
    this.load.image('titan', 'assets/ships/titan.png');
    this.load.image('dreadnaught', 'assets/ships/dreadnaught.png');
    this.load.image('leviathan', 'assets/ships/leviathan.png');
    this.load.image('derelict', 'assets/ships/derelict.png');
    this.load.image('alien-leviathan', 'assets/ships/alien-leviathan.png');

    // Wreck sprites
    this.load.image('wreck-ship', 'assets/ships/wreck-ship.png');
    this.load.image('wreck-station', 'assets/ships/wreck-station.png');

    // Pirate faction ships
    this.load.image('pirate-scout', 'assets/ships/pirate-scout.png');
    this.load.image('pirate-fighter', 'assets/ships/pirate-fighter.png');
    this.load.image('pirate-cruiser', 'assets/ships/pirate-cruiser.png');

    // EDF (Earth Defense Force) ships
    this.load.image('edf-scout', 'assets/ships/edf-scout.png');
    this.load.image('edf-fighter', 'assets/ships/edf-fighter.png');
    this.load.image('edf-cruiser', 'assets/ships/edf-cruiser.png');

    // Ambient entity sprites
    this.load.image('ambient-cargo-convoy', 'assets/ambient/cargo-convoy.png');
    this.load.image('ambient-civilian-transport', 'assets/ambient/civilian-transport.png');
    this.load.image('ambient-science-vessel', 'assets/ambient/science-vessel.png');
    this.load.image('ambient-refugee-ship', 'assets/ambient/refugee-ship.png');
    this.load.image('ambient-patrol-ship', 'assets/ambient/patrol-ship.png');
    this.load.image('ambient-meteor', 'assets/ambient/meteor.png');
    this.load.image('ambient-comet', 'assets/ambient/comet.png');
    this.load.image('ambient-radiation-storm', 'assets/ambient/radiation-storm.png');
    this.load.image('ambient-ion-storm', 'assets/ambient/ion-storm.png');
    this.load.image('ambient-derelict-ship', 'assets/ambient/derelict-ship.png');
    this.load.image('ambient-memorial-beacon', 'assets/ambient/memorial-beacon.png');
    this.load.image('ambient-debris-cluster', 'assets/ambient/debris-cluster.png');

    // Dropped relic sprites (by tier)
    this.load.image('relic-t1', 'assets/relics/relic-t1.png');
    this.load.image('relic-t2', 'assets/relics/relic-t2.png');
    this.load.image('relic-t3', 'assets/relics/relic-t3.png');
    this.load.image('relic-t4', 'assets/relics/relic-t4.png');
    this.load.image('relic-t5', 'assets/relics/relic-t5.png');

    // How To Play guide illustrations
    this.load.image('howto-page1', 'assets/howto/page1-welcome.png');
    this.load.image('howto-page2', 'assets/howto/page2-explore.png');
    this.load.image('howto-page3', 'assets/howto/page3-empire.png');
    this.load.image('howto-page4', 'assets/howto/page4-plunder.png');
    this.load.image('howto-page5', 'assets/howto/page5-playstyle.png');
    this.load.image('howto-page6', 'assets/howto/page6-unknown.png');

    // Action icons (Command Dock)
    this.load.image('icon-move', 'assets/ui/icon-move.png');
    this.load.image('icon-attack', 'assets/ui/icon-attack.png');
    this.load.image('icon-face', 'assets/ui/icon-face.png'); // Also used for dock/undock buttons

    // Ability icons (Command Dock)
    this.load.image('icon-ability-sensor', 'assets/ui/icon-ability-sensor.png');
    this.load.image('icon-ability-evasive', 'assets/ui/icon-ability-evasive.png');
    this.load.image('icon-scan', 'assets/ui/icon-scan.png');
    this.load.image('icon-evade', 'assets/ui/icon-evade.png');
    this.load.image('icon-tow', 'assets/ui/icon-tow.png');

    // Info icons (Command Dock header)
    this.load.image('icon-ship-progress', 'assets/ui/icon-ship-progress.png');
    this.load.image('icon-components', 'assets/ui/icon-components.png');

    // Alliance badge
    this.load.image('alliance-badge', 'assets/ui/alliance-badge.png');

    // Asteroid sprites (used on map + Command Dock)
    this.load.image('asteroid-common', 'assets/asteroids/asteroid-common.png');
    this.load.image('asteroid-iron', 'assets/asteroids/asteroid-iron.png');
    this.load.image('asteroid-copper', 'assets/asteroids/asteroid-copper.png');
    this.load.image('asteroid-ice', 'assets/asteroids/asteroid-ice.png');
    this.load.image('asteroid-platinum', 'assets/asteroids/asteroid-platinum.png');
    this.load.image('asteroid-gold', 'assets/asteroids/asteroid-gold.png');
    this.load.image('asteroid-rare', 'assets/asteroids/asteroid-rare.png');
    this.load.image('asteroid-rare-crystal', 'assets/asteroids/asteroid-rare-crystal.png');
    this.load.image('asteroid-volatile', 'assets/asteroids/asteroid-volatile.png');
    this.load.image('asteroid-dark-matter', 'assets/asteroids/asteroid-dark-matter.png');
    this.load.image('asteroid-exotic', 'assets/asteroids/asteroid-exotic.png');

    // Planet images
    this.load.image('earth', 'assets/sprites/earth.png');
    this.load.image('planet-terra', 'assets/planets/terra.png');
    this.load.image('planet-terra-enhanced', 'assets/planets/terra-enhanced.png');
    this.load.image('planet-pyraxis', 'assets/planets/pyraxis.png');
    this.load.image('planet-virellion', 'assets/planets/virellion.png');
    this.load.image('planet-elaris', 'assets/planets/elaris.png');
    this.load.image('planet-nyxara', 'assets/planets/nyxara.png');
    this.load.image('planet-brasskar', 'assets/planets/brasskar.png');
    this.load.image('planet-geodrys', 'assets/planets/geodrys.png');
    this.load.image('planet-ferronis', 'assets/planets/ferronis.png');
    this.load.image('planet-tempestris', 'assets/planets/tempestris.png');
    this.load.image('planet-mirepox', 'assets/planets/mirepox.png');
    this.load.image('planet-thalora', 'assets/planets/thalora.png');
    this.load.image('planet-cryovale', 'assets/planets/cryovale.png');
    this.load.image('planet-saharrax', 'assets/planets/saharrax.png');
    this.load.image('planet-verdantis', 'assets/planets/verdantis.png');

    // Station images
    this.load.image('station-waypoint', 'assets/stations/waypoint.png');
    this.load.image('station-depot', 'assets/stations/depot.png');
    this.load.image('station-fortress', 'assets/stations/fortress.png');
    this.load.image('station-capital', 'assets/stations/capital.png');

    // Background
    this.load.image('overlay-stars', 'assets/overlay-stars.png');
    this.load.image('figma-stars', 'assets/figma-stars-overlay-56586a.png');

    // Nav bar icons (from Figma design)
    this.load.image('icon-nav-wreck', 'assets/ui/icon-nav-wreck.png');
    this.load.image('icon-nav-trade', 'assets/ui/icon-nav-trade.png');
    this.load.image('icon-nav-ore', 'assets/ui/icon-nav-ore.png');
    this.load.image('icon-nav-crew', 'assets/ui/icon-nav-crew.png');
    this.load.image('icon-nav-parts', 'assets/ui/icon-nav-parts.png');
    this.load.image('icon-nav-shop', 'assets/ui/icon-nav-shop.png');
    this.load.image('icon-nav-station', 'assets/ui/icon-nav-station.png');
    this.load.image('icon-nav-center', 'assets/ui/icon-nav-center.png');
    this.load.image('icon-nav-profile', 'assets/ui/icon-nav-profile.png');
    this.load.image('icon-send', 'assets/ui/icon-send.png');

    // Legend icons for minimap
    this.load.image('icon-legend-planet', 'assets/ui/icon-legend-planet.png');
    this.load.image('icon-legend-asteroid', 'assets/ui/icon-legend-asteroid.png');
    this.load.image('icon-legend-station', 'assets/ui/icon-legend-station.png');

  }
}
